import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import ChatWidget from '../components/chat-widget';
import '../components/twin.module.css';
import '../components/chat-widget.module.css';

type ThemeConfig = Partial<{
  background: string;
  foreground: string;
  accent: string;
  accentHover: string;
  surface: string;
  surfaceBorder: string;
}>;

type ChatConfig = {
  apiUrl?: string;
  targetId?: string;
  theme?: ThemeConfig;
  assetBase?: string;
};

declare global {
  interface Window {
    __ObliqChatConfig?: ChatConfig;
  }
}

const DEFAULT_TARGET_ID = 'obliq-chat-root';

function readConfig(): ChatConfig {
  const script = document.currentScript as HTMLScriptElement | null;
  const dataset = script?.dataset ?? {};
  const initial: ChatConfig = {
    apiUrl: dataset.apiUrl || window.__ObliqChatConfig?.apiUrl,
    targetId: dataset.target || window.__ObliqChatConfig?.targetId || DEFAULT_TARGET_ID,
    theme: { ...window.__ObliqChatConfig?.theme },
    assetBase: window.__ObliqChatConfig?.assetBase,
  };

  const themeKeys: Array<[keyof ThemeConfig, string]> = [
    ['background', 'background'],
    ['foreground', 'foreground'],
    ['accent', 'accent'],
    ['accentHover', 'accentHover'],
    ['surface', 'surface'],
    ['surfaceBorder', 'surfaceBorder'],
  ];

  for (const [key, dataKey] of themeKeys) {
    const value = dataset[`theme${dataKey.charAt(0).toUpperCase()}${dataKey.slice(1)}` as keyof DOMStringMap];
    if (value) {
      if (!initial.theme) initial.theme = {};
      initial.theme[key] = value;
    }
  }

  const assetBase =
    dataset.assetBase ||
    initial.assetBase ||
    (script ? new URL(script.src, window.location.href).origin : window.location.origin);
  initial.assetBase = assetBase?.replace(/\/$/, '');

  window.__ObliqChatConfig = initial;
  return initial;
}

function ensureContainer(targetId: string): HTMLElement {
  let host = document.getElementById(targetId);
  if (!host) {
    host = document.createElement('div');
    host.id = targetId;
    document.body.appendChild(host);
  }
  return host;
}

function applyTheme(element: HTMLElement, theme?: ThemeConfig) {
  if (!theme) return;
  const mapping: Record<keyof ThemeConfig, string> = {
    background: '--twin-background',
    foreground: '--twin-foreground',
    accent: '--twin-accent',
    accentHover: '--twin-accent-hover',
    surface: '--twin-surface',
    surfaceBorder: '--twin-surface-border',
  };

  for (const [key, cssVar] of Object.entries(mapping) as Array<[keyof ThemeConfig, string]>) {
    const value = theme[key];
    if (value) {
      element.style.setProperty(cssVar, value);
    }
  }
}

function mount() {
  const config = readConfig();
  const targetId = config.targetId || DEFAULT_TARGET_ID;

  const host = ensureContainer(targetId);
  applyTheme(host, config.theme);

  const root = createRoot(host);
  root.render(createElement(ChatWidget));

  if (typeof window !== 'undefined') {
    (window as typeof window & { __twinWidgetReady?: boolean }).__twinWidgetReady = true;
    window.dispatchEvent(new Event('twin-widget-ready'));
  }
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  mount();
} else {
  document.addEventListener('DOMContentLoaded', mount);
}
